import {
  ClientRanking,
  Settlement,
  SettlementGroup,
} from 'common/src/utils/types';
import { calculateMarketDifference } from './market-difference.utility';
import { getWeekKey, calculateMedian } from 'common/src/utils/functions';
import { calculateClientDifference } from './client-difference.utility';

export function calculateClientRankings(
  settlements: Settlement[]
): ClientRanking[] {
  const clientStats: Record<string, ClientRanking> = {};
  const ownSettlementsByGroup = groupSettlements(settlements);

  // Generate debug report for RIVERKING
  // if (settlements.some(s => s.receiver === 'RIVERKING')) {
  //   generateClientDebugReport(settlements, 'RIVERKING');
  // }

  settlements.forEach((settlement) => {
    const client = settlement.receiver;
    initializeClientStats(clientStats, client);

    const marketResults = calculateMarketDifference(
      settlement.settlement_details
    );
    const uniqueReceivers = new Set(settlements.map((s) => s.receiver)).size;
    const clientResults =
      uniqueReceivers <= 1
        ? {
            weightedDiff: { byVolume: 0, byFob: 0 },
            comparableKilos: 0,
            comparableFob: 0,
          }
        : calculateClientDifference(settlement, ownSettlementsByGroup);

    updateClientStats(clientStats[client], marketResults, clientResults);
  });

  console.log('ownSettlementsByGroup', ownSettlementsByGroup);
  console.log('clientStats', clientStats);

  const finalRankings = calculateFinalRankings(clientStats);

  console.log('finalRankings', finalRankings);

  return finalRankings;
}

function groupSettlements(settlements: Settlement[]): SettlementGroup {
  const groups: SettlementGroup = {};

  settlements.forEach((settlement) => {
    const groupKey =
      settlement.ship_trip.ship.type === 'A'
        ? `AIR-${getWeekKey(settlement.ship_trip.eta)}-M${settlement.market_id || 'UNKNOWN'}`
        : settlement.ship_trip.ship.type === 'T'
        ? `GROUND-${getWeekKey(settlement.ship_trip.eta)}`
        : `SHIP-${settlement.ship_trip.id}`;

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(settlement);
  });

  return groups;
}

function initializeClientStats(
  clientStats: Record<string, ClientRanking>,
  client: string
) {
  if (!clientStats[client]) {
    clientStats[client] = {
      client,
      totalKilos: 0,
      totalFob: 0,
      totalKilosWithDFMarket: 0,
      totalFobWithDFMarket: 0,
      weightedDifferenceMarket: {
        byVolume: 0,
        byFob: 0,
      },
      weightedDifferenceClients: {
        byVolume: 0,
        byFob: 0,
      },
      totalSettlements: 0,
      comparableKilosClients: 0,
      comparableFobClients: 0,
      clientComparablePercentage: {
        byVolume: 0,
        byFob: 0,
      },
    };
  }
}

function updateClientStats(
  clientStat: ClientRanking,
  marketResults: {
    weightedDiff: { byVolume: number; byFob: number };
    totalKilos: number;
    totalFob: number;
    totalKilosWithDFMarket: number;
    totalFobWithDFMarket: number;
  },
  clientResults: {
    weightedDiff: { byVolume: number; byFob: number };
    comparableKilos: number;
    comparableFob: number;
  }
) {
  if (marketResults.totalKilos > 0) {
    // Acumular las diferencias vs mercado
    clientStat.weightedDifferenceMarket.byVolume +=
      marketResults.weightedDiff.byVolume;
    clientStat.weightedDifferenceMarket.byFob +=
      marketResults.weightedDiff.byFob;

    clientStat.weightedDifferenceClients.byVolume +=
      clientResults.weightedDiff.byVolume;
    clientStat.weightedDifferenceClients.byFob +=
      clientResults.weightedDiff.byFob;

    // Actualizar totales
    clientStat.totalKilos += marketResults.totalKilos;
    clientStat.totalFob += marketResults.totalFob;
    clientStat.totalKilosWithDFMarket += marketResults.totalKilosWithDFMarket;
    clientStat.totalFobWithDFMarket += marketResults.totalFobWithDFMarket;
    clientStat.comparableKilosClients += clientResults.comparableKilos;
    clientStat.comparableFobClients += clientResults.comparableFob;

    clientStat.totalSettlements += 1;
  }
}

function calculateFinalRankings(
  clientStats: Record<string, ClientRanking>
): ClientRanking[] {
  return Object.values(clientStats)
    .map((stat) => {
      return {
        ...stat,
        weightedDifferenceMarket: {
          byVolume:
            stat.totalKilosWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byVolume /
                stat.totalKilosWithDFMarket
              : null,
          byFob:
            stat.totalFobWithDFMarket > 0
              ? stat.weightedDifferenceMarket.byFob / stat.totalFobWithDFMarket
              : null,
        },

        weightedDifferenceClients: {
          byVolume:
            stat.comparableKilosClients > 0
              ? stat.weightedDifferenceClients.byVolume /
                stat.comparableKilosClients
              : null,
          byFob:
            stat.comparableFobClients > 0
              ? stat.weightedDifferenceClients.byFob / stat.comparableFobClients
              : null,
        },

        clientComparablePercentage: {
          byVolume: (stat.comparableKilosClients / stat.totalKilos) * 100,
          byFob: (stat.comparableFobClients / stat.totalFob) * 100,
        },
      };
    })
    .sort(
      (a, b) =>
        b.weightedDifferenceMarket.byVolume -
        a.weightedDifferenceMarket.byVolume
    );
}

function generateClientDebugReport(
  settlements: Settlement[],
  targetClient: string = 'RIVERKING'
): void {
  const ownSettlementsByGroup = groupSettlements(settlements);
  const reportData: any[] = [];
  
  settlements
    .filter(settlement => settlement.receiver === targetClient)
    .forEach((settlement) => {
      settlement.settlement_details?.forEach((detail) => {
        const isAir = settlement.ship_trip.ship.type === 'A';
        const isGround = settlement.ship_trip.ship.type === 'T';
        const groupKey = isAir
          ? `AIR-${getWeekKey(settlement.ship_trip.eta)}-M${settlement.market_id || 'UNKNOWN'}`
          : isGround
          ? `GROUND-${getWeekKey(settlement.ship_trip.eta)}`
          : `SHIP-${settlement.ship_trip.id}`;

        // Get comparable details (same as in calculateClientDifference)
        const groupSettlements = ownSettlementsByGroup[groupKey];
        const comparableDetails = groupSettlements
          ?.map((s) => {
            if (!s.total_freight_usd) return null;
            if (s.receiver === targetClient) return null;

            const matchingDetails =
              s.settlement_details?.filter(
                (d) =>
                  d.variety === detail.variety &&
                  d.caliber === detail.caliber &&
                  d.color === detail.color &&
                  `${d.format}-${d.unit_weight}KG` === `${detail.format}-${detail.unit_weight}KG`
              ) || [];

            if (matchingDetails.length === 0) return null;

            const totalFob = matchingDetails.reduce((sum, d) => sum + d.usd_total_fob, 0);
            const totalKilos = matchingDetails.reduce((sum, d) => sum + d.kilos, 0);

            if (totalKilos <= 0) return null;
            return { 
              client: s.receiver, 
              pricePerKilo: totalFob / totalKilos,
              kilos: totalKilos,
              fob: totalFob
            };
          })
          .filter((value): value is any => value !== null);

        if (comparableDetails && comparableDetails.length > 0) {
          const prices = comparableDetails.map(cd => cd.pricePerKilo);
          const median = calculateMedian(prices);
          const currentFobKilo = detail.usd_total_fob / detail.kilos;
          
          // Use the same fixed percentage calculation logic
          let percentDiff: number;
          if (median === 0) {
            percentDiff = 0;
          } else if (median > 0) {
            // Standard case: positive median
            percentDiff = ((currentFobKilo - median) / median) * 100;
          } else {
            // Negative median case: handle properly
            if (currentFobKilo >= 0) {
              // Current is positive/zero, median is negative -> current is much better
              percentDiff = Math.abs(currentFobKilo - median) / Math.abs(median) * 100;
            } else {
              // Both are negative: closer to zero = better performance
              percentDiff = ((currentFobKilo - median) / Math.abs(median)) * 100;
            }
          }
          
          const volumeContribution = percentDiff * detail.kilos;
          const fobContribution = percentDiff * detail.usd_total_fob;

          reportData.push({
            settlement_id: settlement.id,
            detail_id: detail.id,
            ship_trip_id: settlement.ship_trip.id,
            ship_type: settlement.ship_trip.ship.type,
            eta: settlement.ship_trip.eta,
            group_key: groupKey,
            variety: detail.variety,
            caliber: detail.caliber,
            color: detail.color,
            format: detail.format,
            unit_weight: detail.unit_weight,
            kilos: detail.kilos,
            usd_total_fob: detail.usd_total_fob,
            price_per_kilo: currentFobKilo,
            comparable_clients: comparableDetails.map(cd => `${cd.client}:${cd.pricePerKilo.toFixed(2)}`).join('; '),
            comparable_count: comparableDetails.length,
            median_price: median,
            percent_diff: percentDiff,
            volume_contribution: volumeContribution,
            fob_contribution: fobContribution,
            running_volume_total: 0, // Will be calculated
            running_fob_total: 0     // Will be calculated
          });
        }
      });
    });

  // Calculate running totals
  let runningVolumeTotal = 0;
  let runningFobTotal = 0;
  reportData.forEach(row => {
    runningVolumeTotal += row.volume_contribution;
    runningFobTotal += row.fob_contribution;
    row.running_volume_total = runningVolumeTotal;
    row.running_fob_total = runningFobTotal;
  });

  // Generate CSV content
  if (reportData.length > 0) {
    const headers = Object.keys(reportData[0]);
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Wrap strings containing commas in quotes
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${targetClient}_client_comparison_debug.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`Generated debug report for ${targetClient} with ${reportData.length} comparable details`);
    console.log(`Final volume total: ${runningVolumeTotal}`);
    console.log(`Final FOB total: ${runningFobTotal}`);
  } else {
    console.log(`No comparable details found for ${targetClient}`);
  }
}
